import React, { useState, useEffect } from 'react';
import './RoomPage.css';
import { useNavigate } from 'react-router-dom';
import {
  db,
  collection,
  doc,
  setDoc,
  getDoc,
  Timestamp,
  serverTimestamp,
  onSnapshot ,
  updateDoc
} from '../firebase';
import { getAuth } from 'firebase/auth';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';

const RoomPage = () => {
  const [roomIdInput, setRoomIdInput] = useState('');
  const [error, setError] = useState('');
  const [uid, setUid] = useState(null);
  const [dp, setDp] = useState(null);
  const [savedDecks, setSavedDecks] = useState([]);
  const [selectedDeckIndex, setSelectedDeckIndex] = useState(null);
  const [pseudo, setPseudo] = useState('');
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);


  const navigate = useNavigate();
  const auth = getAuth();


  function shuffleArray(array) {
    const copy = [...array];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUid(user.uid);
      } else {
        setUid(null);
        setDp(null);
        setPseudo('');
      }
    });
    return () => unsubscribe();
  }, []);


  // ✅ Load saved decks on mount
  useEffect(() => {
    const local = JSON.parse(localStorage.getItem('savedDecks') || '[]');
    setSavedDecks(local);

    // Try to preselect handTestDeck if it exists
    const handTest = JSON.parse(localStorage.getItem('handTestDeck') || 'null');
    if (handTest) {
      const foundIndex = local.findIndex(deck => deck.name === handTest.name);
      if (foundIndex !== -1) setSelectedDeckIndex(foundIndex);
    }
  }, []);

  // ✅ Fetch user info
  useEffect(() => {
    if (!uid) return;

    const unsubscribe = onSnapshot(doc(db, 'users', uid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setDp(data.dp);
        setPseudo(data.pseudo || '');
      }
    });

    return () => unsubscribe();
  }, [uid]);


  // ✅ Create Room
  const createRoom = async () => {
    if (!uid) return setError('Please log in first.');
    

    const deckData = savedDecks[selectedDeckIndex];
    const sanitizedDeckData = {
      hand: deckData?.hand || [],
      main: deckData?.main || [],
      extra: deckData?.extra || []
    };
    const shuffledMain = shuffleArray(sanitizedDeckData.main);
    const initialHand = shuffledMain.slice(0, 5);
    const remainingMainDeck = shuffledMain.slice(5);



    if (!deckData) {
      alert('❗ Please select a deck first!');
      return;
    }

    // Store the selected deck in localStorage for PvpOnline
    localStorage.setItem('handTestDeck', JSON.stringify(deckData));

    const roomId = uuidv4().slice(0, 6).toUpperCase();
    const roomRef = doc(collection(db, 'duelRooms'), roomId);

    await setDoc(roomRef, {
     

      host: {
        uid: uid,
        pseudo: pseudo || "Host"
      },
      hostUid: uid,
      status: 'waiting',
      createdAt: serverTimestamp(),
      expireAt: Timestamp.fromDate(new Date(Date.now() + 3 * 60 * 60 * 1000)),
      gameState: {
        host: {
          fieldMap: {},
          hand: initialHand,
          mainDeck: remainingMainDeck,
          extraDeck: deckData.extra || [],
          gy: [],
          bz: [],
          isInitialized: true
        },
        guest: {
          fieldMap: {},
          hand: [],
          mainDeck: [],
          extraDeck: [],
          gy: [],
          bz: [],
          isInitialized: true
        },
        turn: 'host',
        updatedAt: serverTimestamp()
      }

    });

    localStorage.setItem('duelRoomId', roomId);
    localStorage.setItem('uid', uid);
    navigate(`/online-duel/${roomId}`);
  };

  // ✅ Join Room
  const joinRoom = async () => {
    if (!uid) return setError('Please log in first.');

    const deckData = savedDecks[selectedDeckIndex];
    const sanitizedDeckData = {
      hand: deckData?.hand || [],
      main: deckData?.main || [],
      extra: deckData?.extra || []
    };
    const shuffledMain = shuffleArray(sanitizedDeckData.main);
    const initialHand = shuffledMain.slice(0, 5);
    const remainingMainDeck = shuffledMain.slice(5);




    if (!deckData) {
      alert('❗ Please select a deck first!');
      return;
    }

    // Store the selected deck in localStorage for PvpOnline
    localStorage.setItem('handTestDeck', JSON.stringify(deckData));

    const roomRef = doc(db, 'duelRooms', roomIdInput.trim());
    const roomSnap = await getDoc(roomRef);
    if (!roomSnap.exists()) return setError('Room not found.');

    const roomData = roomSnap.data();
    if (roomData.status !== 'waiting') return setError('Room is full or in progress.');

    await updateDoc(roomRef, {
      guestUid: uid,
      status: 'full',
      [`gameState.guest`]: {
        fieldMap: {},
        hand: initialHand,
        mainDeck: remainingMainDeck,
        extraDeck: sanitizedDeckData.extra,
        gy: [],
        bz: [],
        isInitialized: true
      },
      'gameState.updatedAt': serverTimestamp()
    });

    localStorage.setItem('duelRoomId', roomIdInput.trim());
    localStorage.setItem('uid', uid);
    navigate(`/online-duel/${roomIdInput.trim()}`);
  };

  return (
    <div className="room-page">
      <h1>Duel Room</h1>
      <button className="back-button" onClick={() => navigate(-1)}>Back</button>

      {/* ✅ Deck selection */}
      <div style={{ marginBottom: '16px' }}>
        <label><strong>Select Your Deck:</strong></label>
        <select
          value={selectedDeckIndex ?? ''}
          onChange={(e) => setSelectedDeckIndex(Number(e.target.value))}
          style={{ marginLeft: '8px' }}
        >
          <option value="" disabled>Select a deck</option>
          {savedDecks.map((deck, idx) => (
            <option key={idx} value={idx}>
              {deck.name}
            </option>
          ))}
        </select>
      </div>

      {/* ✅ Create room button */}
      <button className="room-page-button" onClick={createRoom} disabled={selectedDeckIndex === null}>
        Create Room
      </button>

      <hr />

      {/* ✅ Join room inputs */}
      <input
        type="text"
        placeholder="Enter Room ID"
        value={roomIdInput}
        onChange={(e) => setRoomIdInput(e.target.value.toUpperCase())}
      />
      <button className="room-page-button" onClick={joinRoom} disabled={selectedDeckIndex === null || !roomIdInput.trim()}>
        Join Room
      </button>

      {error && <div className="error-message">{error}</div>}

      <div>
        <p><strong>Username:</strong> {pseudo || 'Not logged in'}</p>
        <p><strong>Duelist Points (DP):</strong> {dp ?? 'Loading...'}</p>
      </div>

    </div>
  );
};

export default RoomPage;
